import { computed, unref, type Ref } from 'vue'
import { useAsyncData } from 'nuxt/app';
import builderIoService from '~/composables/builderIoService'
import { useLocaleStore } from '~/stores/localeStore'
import type { BuilderEntryWithId, ProductFaqEntryData } from '~~/shared/types/common'

export const useProductFaqExpandable = (
  sku: string | Ref<string | undefined> | undefined
) => {
  const service = builderIoService()
  const localeStore = useLocaleStore()

  const sanitizedSku = computed(() => {
    const rawSku = unref(sku)

    if (typeof rawSku !== 'string') return undefined

    return rawSku.replaceAll(/(^["']+)|(["']+$)/g, '').trim()
  })

  const countryCode = computed(() => {
    const code = (localeStore.locale as any)?.country?.code
    return typeof code === 'string' && code.trim()
      ? code.trim().toUpperCase()
      : 'US'
  })

  const { data, error, pending, refresh } = useAsyncData(
    () => `product-faqs-${sanitizedSku.value ?? 'empty'}`,
    async () => {
      if (!sanitizedSku.value) {
        return { faqs: [] }
      }

      const query = {
        $and: [
          { 'data.productSku': sanitizedSku.value },
          { 'data.status': 'Active' },

          {
            $or: [
              { 'data.blacklistCountryRestrictions': { $exists: false } },
              { 'data.blacklistCountryRestrictions': { $size: 0 } },
              {
                'data.blacklistCountryRestrictions.countryCode': {
                  $nin: [countryCode.value],
                },
              },
            ],
          },

          {
            $or: [
              { 'data.whitelistCountryRestrictions': { $exists: false } },
              { 'data.whitelistCountryRestrictions': { $size: 0 } },
              {
                'data.whitelistCountryRestrictions.countryCode': {
                  $in: [countryCode.value],
                },
              },
            ],
          },
        ],
      }

      const fetchOptions: any = {
        limit: 1,
        query,
        enrich: true,
        cachebust: true,
      }

      const result = await service.fetchManyModels(
        'product-faqs',
        '/',
        false,
        fetchOptions
      )

      const entry = Array.isArray(result)
        ? (result[0] as BuilderEntryWithId<ProductFaqEntryData>)
        : null

      const faqsArray = entry?.data?.faqs ?? []

      return {
        faqs: faqsArray.map((faq) => ({
          question: faq.question,
          answer: faq.answer,
          open: false,
        })),
      }
    },
    {
      server: true,
      lazy: false,
      default: () => ({ faqs: [] }),
      watch: [sanitizedSku, countryCode],
    }
  )

  const faqs = computed(() => data?.value?.faqs ?? [])
  return {
    faqs,
    faqsPending: pending,
    error,
    refresh,
  }
}
